<template>
  <!-- rider waitlist portal start -->
  <div class="container-fluid p-0">
    <div class="row m-0">
      <div class="col-12 p-0">
        <div class="login-card login-dark">
          <div>
            <div>
              <a class="logo" href="/portal">
                <img class="img-fluid for-light m-auto" src="/assets/images/logo.svg" alt="Asher"/>
                <img class="img-fluid for-dark m-auto" src="/assets/images/logo.svg" alt="Asher"/>
              </a>
            </div>
            <div class="login-main">
              <RiderCompleteProfileForm v-if="mode === 'complete-profile'" />
              <template v-else>
              <h2 class="text-center mb-1 fw-bold">
                {{ mode === 'signup' ? 'Join the waitlist' : 'Welcome back' }}
              </h2>
              <p class="text-center text-muted">
                {{
                  mode === 'signup'
                      ? 'Sign up to reserve your spot on the Asher waitlist.'
                      : 'Sign in to view your waitlist status.'
                }}
              </p>

              <!-- Feedback banners -->
              <div v-if="errorMessage" class="alert alert-danger py-2" role="alert">
                {{ errorMessage }}
                <div v-if="notRegistered" class="mt-2">
                  <button type="button" class="btn btn-sm btn-primary" @click="switchMode('signup')">
                    Sign up now
                  </button>
                </div>
              </div>
              <div v-if="infoMessage" class="alert alert-info py-2" role="alert">{{ infoMessage }}</div>

                <!-- SSO first (parity with rider app) -->
                <div class="sso-buttons mx-auto">
                  <div v-if="googleClientId" ref="googleButtonContainer" class="google-signin-container"></div>
                  <button v-else
                          class="btn btn-outline-dark sso-button d-flex align-items-center justify-content-center gap-2"
                          disabled>
                    <i class="fab fa-google"></i> Google
                  </button>
                  <button v-if="appleServiceId"
                          class="btn btn-outline-dark sso-button d-flex align-items-center justify-content-center gap-2"
                          :disabled="isBusy"
                          @click="handleAppleSignIn">
                    <span v-if="isBusy" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="fab fa-apple"></i> Apple
                  </button>
                </div>
                <p class="text-center text-muted small mt-2 mb-0">
                  By continuing with Google or Apple, you agree to our
                  <a href="https://asherrides.notion.site/terms-and-conditions?pvs=143" target="_blank">Terms of Service</a>
                and <a href="https://asherrides.notion.site/privacy-policy?pvs=143" target="_blank">Privacy Policy</a>.
              </p>

              <div class="login-or-bar text-center my-3">
                <span class="px-2 text-muted">OR</span>
              </div>

              <!-- Channel toggle -->
              <div class="btn-group w-100 mb-3" role="group" aria-label="Verification channel">
                <button type="button" class="btn"
                        :class="channel === 'phone' ? 'btn-primary' : 'btn-outline-primary'"
                        :disabled="isBusy" @click="setChannel('phone')">
                  <i class="fa fa-phone me-1"></i> Phone
                </button>
                <button type="button" class="btn"
                        :class="channel === 'email' ? 'btn-primary' : 'btn-outline-primary'"
                        :disabled="isBusy" @click="setChannel('email')">
                  <i class="fa fa-envelope me-1"></i> Email
                </button>
              </div>

              <form class="theme-form" @submit.prevent="onPrimarySubmit">
                <!-- Signup-only name fields -->
                <template v-if="mode === 'signup'">
                  <div class="form-group">
                    <label class="col-form-label">First name</label>
                    <input class="form-control" type="text" v-model.trim="firstName"
                           :disabled="otpSent" placeholder="First name"/>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Last name</label>
                    <input class="form-control" type="text" v-model.trim="lastName"
                           :disabled="otpSent" placeholder="Last name"/>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Referral code <span class="text-muted">(optional)</span></label>
                    <input class="form-control" type="text" v-model.trim="referralCode"
                           :disabled="otpSent" placeholder="Have a friend's code?"/>
                  </div>
                </template>

                <!-- Identifier: email -->
                <div v-if="channel === 'email'" class="form-group">
                  <label class="col-form-label">Email address</label>
                  <input class="form-control" type="email" v-model.trim="email"
                         :disabled="otpSent" placeholder="email@address.com"/>
                </div>

                <!-- Identifier: phone with country-code dropdown (matches driver signup) -->
                <div v-else class="form-group">
                  <label class="col-form-label">Phone number</label>
                  <div class="input-group">
                    <select class="form-select" v-model="countryCode" :disabled="otpSent" style="max-width: 120px;">
                      <option value="+1">+1 (US)</option>
                      <option value="+1">+1 (CA)</option>
                    </select>
                    <input class="form-control" type="tel" v-model.trim="phoneNumber"
                           :disabled="otpSent" placeholder="5551234567"/>
                  </div>
                  <small class="text-muted">Select country code and enter the rest of your phone number.</small>
                </div>

                <!-- Signup terms (phone/email signup only; SSO consent is implicit — see the
                     disclaimer under the SSO buttons above) -->
                <div v-if="mode === 'signup'" class="form-group form-check mt-2">
                  <input class="form-check-input" type="checkbox" id="agreeTerms"
                         v-model="agreedToLegalPolicy" :disabled="otpSent"/>
                  <label class="form-check-label" for="agreeTerms">
                    I agree to the Terms of Service and Privacy Policy.
                  </label>
                </div>

                <!-- OTP entry (after code sent) -->
                <div v-if="otpSent" class="form-group">
                  <label class="col-form-label">Verification code</label>
                  <input class="form-control" type="text" inputmode="numeric" v-model.trim="otpCode"
                         placeholder="Enter the code we sent you"/>
                  <div class="text-end mt-2">
                    <button type="button" class="btn btn-link p-0"
                            :disabled="resendSeconds > 0 || isBusy" @click="sendCode">
                      {{ resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend code' }}
                    </button>
                  </div>
                </div>

                <!-- Primary action -->
                <div class="text-end mt-3">
                  <button class="btn btn-primary btn-block w-100" type="submit" :disabled="isBusy">
                    <span v-if="isBusy" class="spinner-border spinner-border-sm me-2"></span>
                    {{ primaryButtonLabel }}
                  </button>
                </div>
              </form>

              <!-- Mode switch -->
              <p class="text-center text-muted mt-4 mb-0">
                <template v-if="mode === 'login'">
                  Don't have an account?
                  <a href="#" @click.prevent="switchMode('signup')" class="fw-bold">Sign up</a>
                </template>
                <template v-else>
                  Already on the waitlist?
                  <a href="#" @click.prevent="switchMode('login')" class="fw-bold">Log in</a>
                </template>
              </p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, onMounted, onUnmounted} from 'vue';
import RiderCompleteProfileForm from './RiderCompleteProfileForm.vue';
import {RIDER_VERIFY_NUDGE_DISMISS_KEY} from '../utility/riderVerifyNudge.js';

// Must match the .sso-buttons width in <style>; Google's SDK renders at a fixed
// pixel width rather than filling its container.
const SSO_BUTTON_WIDTH_PX = 280;

const props = defineProps({
  googleClientId: {type: String, default: ''},
  appleServiceId: {type: String, default: ''},
  appleRedirectUri: {type: String, default: ''},
});

const mode = ref('login');        // 'login' | 'signup' | 'complete-profile'
const channel = ref('phone');     // 'phone' | 'email'
const firstName = ref('');
const lastName = ref('');
const referralCode = ref('');
const email = ref('');
const countryCode = ref('+1');
const phoneNumber = ref('');
const agreedToLegalPolicy = ref(false);
const otpCode = ref('');
const otpSent = ref(false);
const loading = ref(false);
const resendSeconds = ref(0);
const errorMessage = ref(null);
const infoMessage = ref(null);
// Set when the login identifier has no account, so the error banner can offer the signup form
// with whatever the visitor already typed still in place.
const notRegistered = ref(false);
const googleButtonContainer = ref(null);

let resendTimer = null;

const isEmail = computed(() => channel.value === 'email');
const isBusy = computed(() => loading.value);

// The contact sent to the backend: raw email, or country code + phone digits.
const effectiveIdentifier = computed(() =>
    isEmail.value ? email.value : `${countryCode.value}${phoneNumber.value}`);

const primaryButtonLabel = computed(() => {
  if (!otpSent.value) return 'Send code';
  return mode.value === 'signup' ? 'Join the waitlist' : 'Log in';
});

onMounted(() => {
  const refParam = new URLSearchParams(window.location.search).get('ref');
  if (refParam) referralCode.value = refParam;
  initGoogleSignIn();
  initAppleSignIn();
});

function resetFlow() {
  otpSent.value = false;
  otpCode.value = '';
  errorMessage.value = null;
  infoMessage.value = null;
  notRegistered.value = false;
  stopResend();
}

function switchMode(next) {
  mode.value = next;
  resetFlow();
}

function setChannel(next) {
  if (otpSent.value) return; // don't switch mid-verification
  channel.value = next;
  errorMessage.value = null;
}

function startResendCountdown(seconds = 30) {
  stopResend();
  resendSeconds.value = seconds;
  resendTimer = setInterval(() => {
    if (resendSeconds.value > 0) resendSeconds.value -= 1;
    else stopResend();
  }, 1000);
}

function stopResend() {
  if (resendTimer) {
    clearInterval(resendTimer);
    resendTimer = null;
  }
}

async function postJson(url, payload) {
  const res = await fetch(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(payload),
  });
  let data = null;
  try {
    data = await res.json();
  } catch (e) { /* may be a plain error string */
  }
  if (!res.ok) {
    const text = data ? (data.message || '') : await res.text().catch(() => '');
    throw new Error(text || 'Something went wrong. Please try again.');
  }
  return data || {};
}

function onPrimarySubmit() {
  if (otpSent.value) {
    mode.value === 'signup' ? submitSignup() : submitLogin();
  } else {
    sendCode();
  }
}

async function sendCode() {
  errorMessage.value = null;
  infoMessage.value = null;
  notRegistered.value = false;

  if (isEmail.value ? !email.value : !phoneNumber.value) {
    errorMessage.value = isEmail.value ? 'Please enter your email.' : 'Please enter your phone number.';
    return;
  }
  if (mode.value === 'signup' && (!firstName.value || !lastName.value || !agreedToLegalPolicy.value)) {
    errorMessage.value = 'Please enter your name and agree to the terms.';
    return;
  }

  loading.value = true;
  try {
    const url = mode.value === 'signup'
        ? '/api/portal/rider/send-signup-otp'
        : '/api/portal/rider/send-login-otp';
    const data = await postJson(url, {userIdentifier: effectiveIdentifier.value, isEmail: isEmail.value});
    if (data.success === false) {
      errorMessage.value = data.message || 'Unable to send verification code.';
      notRegistered.value = data.code === 'not_registered';
      return;
    }
    otpSent.value = true;
    infoMessage.value = data.message || 'Verification code sent.';
    startResendCountdown(30);
  } catch (e) {
    errorMessage.value = e.message;
  } finally {
    loading.value = false;
  }
}

async function submitSignup() {
  errorMessage.value = null;
  if (!otpCode.value) {
    errorMessage.value = 'Please enter the verification code.';
    return;
  }

  loading.value = true;
  try {
    const payload = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: isEmail.value ? email.value : null,
      phoneNumber: isEmail.value ? null : effectiveIdentifier.value,
      otpCode: otpCode.value,
      otpIsEmail: isEmail.value,
      agreedToLegalPolicy: agreedToLegalPolicy.value,
      referralCode: referralCode.value || null,
    };
    const data = await postJson('/api/portal/rider/signup', payload);
    if (data.success && data.redirectUrl) {
      // A fresh login-like landing always re-arms the verify-contact nudge, even if it was
      // dismissed in an earlier session.
      sessionStorage.removeItem(RIDER_VERIFY_NUDGE_DISMISS_KEY);
      window.location.href = data.redirectUrl;
    } else {
      errorMessage.value = data.message || 'Unable to complete signup.';
    }
  } catch (e) {
    errorMessage.value = e.message;
  } finally {
    loading.value = false;
  }
}

async function submitLogin() {
  errorMessage.value = null;
  if (!otpCode.value) {
    errorMessage.value = 'Please enter the verification code.';
    return;
  }

  loading.value = true;
  try {
    const data = await postJson('/api/portal/rider/login', {
      userIdentifier: effectiveIdentifier.value,
      isEmail: isEmail.value,
      otpCode: otpCode.value,
    });
    if (data.success && data.redirectUrl) {
      // A fresh login-like landing always re-arms the verify-contact nudge, even if it was
      // dismissed in an earlier session.
      sessionStorage.removeItem(RIDER_VERIFY_NUDGE_DISMISS_KEY);
      window.location.href = data.redirectUrl;
    } else {
      errorMessage.value = data.message || 'Invalid verification code.';
    }
  } catch (e) {
    errorMessage.value = e.message;
  } finally {
    loading.value = false;
  }
}

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function initGoogleSignIn() {
  if (!props.googleClientId || !googleButtonContainer.value) return;
  try {
    await loadScriptOnce('https://accounts.google.com/gsi/client');
    window.google.accounts.id.initialize({
      client_id: props.googleClientId,
      callback: handleGoogleCredential,
    });
    window.google.accounts.id.renderButton(googleButtonContainer.value, {
      theme: 'outline',
      size: 'large',
      shape: 'rectangular',
      text: 'signin_with',
      width: SSO_BUTTON_WIDTH_PX,
    });
  } catch (e) {
    errorMessage.value = 'Unable to load Google sign-in right now.';
  }
}

async function handleGoogleCredential(response) {
  errorMessage.value = null;
  infoMessage.value = null;
  loading.value = true;
  try {
    await submitSso('google', response.credential);
  } finally {
    loading.value = false;
  }
}

async function initAppleSignIn() {
  if (!props.appleServiceId) return;
  try {
    await loadScriptOnce('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
    window.AppleID.auth.init({
      clientId: props.appleServiceId,
      scope: 'name email',
      redirectURI: props.appleRedirectUri,
      usePopup: true,
    });
  } catch (e) {
    errorMessage.value = 'Unable to load Apple sign-in right now.';
  }
}

async function handleAppleSignIn() {
  errorMessage.value = null;
  infoMessage.value = null;
  loading.value = true;
  try {
    const result = await window.AppleID.auth.signIn();
    await submitSso('apple', result.authorization.id_token);
  } catch (e) {
    if (!e || e.error !== 'popup_closed_by_user') {
      errorMessage.value = 'Apple sign-in failed. Please try again.';
    }
  } finally {
    loading.value = false;
  }
}

async function submitSso(provider, idToken) {
  // SSO consent is implicit, via the disclaimer shown under the Google/Apple buttons —
  // not gated on the phone/email signup form's explicit checkbox.
  const data = await postJson('/api/portal/rider/sso', {
    provider,
    idToken,
    agreedToLegalPolicy: true,
  });
  if (data.success && data.redirectUrl) {
    // A fresh login-like landing always re-arms the verify-contact nudge, even if it was
    // dismissed in an earlier session.
    sessionStorage.removeItem(RIDER_VERIFY_NUDGE_DISMISS_KEY);
    window.location.href = data.redirectUrl;
  } else if (data.success && data.profileComplete === false) {
    mode.value = 'complete-profile';
  } else {
    errorMessage.value = data.message || 'Unable to complete sign-in.';
  }
}

onUnmounted(stopResend);
</script>

<style scoped>
.sso-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 280px;
  max-width: 100%;
}

.sso-button {
  width: 100%;
  height: 40px;
}

.google-signin-container {
  width: 100%;
  min-height: 40px;
  display: flex;
  justify-content: center;
}

.login-or-bar {
  position: relative;
}

.login-or-bar::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--bs-border-color, #e6e6e6);
  z-index: 0;
}

.login-or-bar span {
  position: relative;
  z-index: 1;
  background: #fff;
}
</style>
